import { HoleMechanicalProcess, PcbNodeTypes, PcbViaType, getOrderedCopperLayers, } from "@buildwithflux/models";
import { humanizeLengthValue } from "@buildwithflux/shared";
import { maxBy } from "lodash";
import { Vector2 } from "three";
import { calcKeepoutForNodeAndType } from "../fills/keepOutHelpers";
import { RuleParser } from "../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleParser";
/**
 * Checks whether a particular via segment is connected to the specified layer
 * @param segment Via config segment
 * @param layerOrder Layer order in the stackup (index + 1)
 * @returns Whether the layer is connected to the segment
 */
export function isViaSegmentConnectingLayer(segment, layerOrder) {
    return segment.mechanicalProcess === HoleMechanicalProcess.drill
        ? // if segment is drilled, the layer falls in between the drill segment
            layerOrder >= segment.startLayer && layerOrder <= segment.endLayer
        : // or if laser, the layer is either the start or end layer
            layerOrder === segment.startLayer || layerOrder === segment.endLayer;
}
/**
 * Returns the via segment information from the via type
 * @param viaType "viaType" layout/baked rule
 * @param viaConfigs All via configurations
 * @returns Via segment information
 */
export function getViaSegmentFromType(viaType, viaConfigs) {
    // Via type is in the format of "{viaConfigUid}-{segmentIndex}"
    const splitByDash = viaType.split("-");
    const lastPart = splitByDash.pop();
    if (!lastPart) {
        return;
    }
    const segmentIndex = parseInt(lastPart) - 1;
    const viaConfigUid = splitByDash.join("-");
    const viaConfigIndex = viaConfigs.findIndex((config) => config.uid === viaConfigUid);
    const viaConfig = viaConfigs[viaConfigIndex];
    const segment = viaConfig?.segments[segmentIndex];
    if (!segment) {
        return;
    }
    return {
        viaConfig,
        segment,
        viaConfigIndex,
        segmentIndex,
    };
}
/**
 * Returns the canonical type of a via segment (throughHole, buried, blind, microVia)
 */
export function getViaSegmentCanonicalType(segment, copperLayers) {
    // Micro-via case
    if (segment.mechanicalProcess === HoleMechanicalProcess.laser) {
        return PcbViaType.microVia;
    }
    if (segment.mechanicalProcess === HoleMechanicalProcess.drill) {
        // If contact with surface from start layer side, and end layer is within copper
        // layer count then we're dealing with a blind via
        if (segment.startLayer === 1 && segment.endLayer < copperLayers.length) {
            return PcbViaType.blind;
        }
        // .. or, if contact with surface from end layer side, and start layer is within copper
        // layer count then in this case also, we're dealing with a blind via
        if (segment.startLayer > 1 && segment.endLayer === copperLayers.length) {
            return PcbViaType.blind;
        }
        // If layer range is fully contained in the copper layer range, that implies a buried via
        if (segment.startLayer > 1 && segment.endLayer < copperLayers.length) {
            return PcbViaType.buried;
        }
    }
    // If we're here, it implies start/end layer range cover entire copper layer range (or an invalid
    // mechanical process) in which case we fallback to through hole
    return PcbViaType.throughHole;
}
/**
 * Checks if a via configuration is a through-hole via configuration.
 * A configuration is considered a through-hole if it has only one segment
 * and that segment is a through-hole type.
 */
export function isThroughHoleViaConfig(viaConfig, copperLayers) {
    const [segment, ...restSegments] = viaConfig.segments;
    if (!segment) {
        return false;
    }
    // If there are multiple segments, it implies non-through-hole via config
    if (restSegments.length) {
        return false;
    }
    return getViaSegmentCanonicalType(segment, copperLayers) === PcbViaType.throughHole;
}
/**
 * Get the resolved hole config rule values for a via segment.
 */
export function getViaSegmentResolvedSizeValues(viaSegment, viaHoleConfigs, unit) {
    switch (viaSegment.mechanicalProcess) {
        // For regular drill holes, we just return the size and hole size as is
        default:
        case HoleMechanicalProcess.drill: {
            return {
                size: viaSegment.size,
                holeSize: viaSegment.holeSize,
            };
        }
        // Special clamping needs to be done for laser holes
        // Rules:
        // * holeSize must be at least 15um
        // * size must be at least 15um larger than holeSize
        // * If a minHoleSize is specified, it must be at least 15um
        // * If a minSize is specified, it must be at least 15um larger than minHoleSize
        // * If a minHoleSize is not specified, it defaults to 15um
        // * If a minSize is not specified, it defaults to 15um larger than minHoleSize (30um)
        //
        // All values are first reduced to a normalized form for comparisons
        // and then humanized back for the rule set using the node's baked unit
        //
        // This is a temporary solution. In the future, we want to consider using
        // multiple system-default selectors that target via nodes using their drill process type
        // and set appropriate clamping rules in the selectors' rule set.
        case HoleMechanicalProcess.laser: {
            const DEFAULT_MIN_HOLE_SIZE = 0.000015; // 15um
            const DEFAULT_SIZE_INCREMENENT_OVER_HOLE_SIZE = 0.000015; // 15um
            // Get this via segments user-specified size and hole size values
            // These might need to be clamped based on the hole configs
            const viaSegmentSize = getNormalizedLengthValue(viaSegment.size);
            const viaSegmentHoleSize = getNormalizedLengthValue(viaSegment.holeSize);
            // Get the minimum hole size and size values from the user-specified hole configs
            let [minSpecifiedHoleSize, minSpecifiedSize] = viaHoleConfigs
                .filter((hole) => hole.mechanicalProcess === HoleMechanicalProcess.laser)
                .reduce((minValues, holeConfig) => {
                if (minValues[0] === undefined || holeConfig.holeSize < minValues[0]) {
                    minValues[0] = holeConfig.holeSize;
                }
                if (minValues[1] === undefined || holeConfig.minSize < minValues[1]) {
                    minValues[1] = holeConfig.minSize;
                }
                return minValues;
            }, []);
            // Bogus or undefined values are reduced to 0 so we can resolve them to the defaults
            minSpecifiedHoleSize = Math.max(minSpecifiedHoleSize ?? 0, DEFAULT_MIN_HOLE_SIZE);
            minSpecifiedSize = Math.max(minSpecifiedSize ?? 0, minSpecifiedHoleSize + DEFAULT_SIZE_INCREMENENT_OVER_HOLE_SIZE);
            // Finally, clamp the viaSegment size and hole size values with the resolved min values
            const resolvedSize = Math.max(viaSegmentSize, minSpecifiedSize);
            const resolvedHoleSize = Math.max(viaSegmentHoleSize, minSpecifiedHoleSize);
            // Humanize before adding to rule set using the node's baked unit
            const humanizedSize = humanizeLengthValue(resolvedSize, unit);
            const humanizedHoleSize = humanizeLengthValue(resolvedHoleSize, unit);
            const humanizedMinSize = humanizeLengthValue(minSpecifiedSize, unit);
            const humanizedMinHoleSize = humanizeLengthValue(minSpecifiedHoleSize, unit);
            return {
                size: humanizedSize,
                minSizeX: humanizedMinSize,
                minSizeY: humanizedMinSize,
                holeSize: humanizedHoleSize,
                minHoleSize: humanizedMinHoleSize,
            };
        }
    }
}
/**
 * Extracts the baked values of size and hole size from a via segment
 */
function getSegmentSizeAndHoleSize(segment) {
    const parser = new RuleParser({
        size: { value: segment.size, uid: "size", key: "size" },
        holeSize: { value: segment.holeSize, uid: "holeSize", key: "holeSize" },
    });
    const { size, holeSize } = parser.parseRules(["size", "holeSize"]);
    return { size, holeSize };
}
export function getProjectedChildrenPositions(smartViaNode, smartViaBakedRules) {
    // Resolve overrides and early return if no baked rules found
    smartViaBakedRules ??= "bakedRules" in smartViaNode ? smartViaNode.bakedRules : undefined;
    if (!smartViaBakedRules) {
        return [new Vector2(), new Vector2()];
    }
    // Calculate keepout for the smart via node
    // Default keepout value is stored in the DefaultRuleSheet
    // TODO: bad usage of keepout functions vs type
    const keepout = calcKeepoutForNodeAndType(smartViaNode, smartViaBakedRules, PcbNodeTypes.via);
    // Group segments of staggered configs in alternate bins/positions
    const segmentsGroupedByPosition = [];
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    smartViaBakedRules.selectedViaConfigs.forEach((viaConfig, index) => {
        const positionIndex = index % 2;
        const currentSegments = segmentsGroupedByPosition[positionIndex] ?? [];
        segmentsGroupedByPosition[positionIndex] = [...currentSegments, ...viaConfig.segments];
    });
    // Find the largest segment at each bin/position
    const largestSegmentsAtPosition = segmentsGroupedByPosition.map((segments) => maxBy(segments, (segment) => {
        const { holeSize } = getSegmentSizeAndHoleSize(segment);
        return Math.max(holeSize.x, holeSize.y);
    }));
    // Calculate the spacing between the largest segments at each position
    const spacingBetweenLargestSegments = [];
    for (let index = 1; index < largestSegmentsAtPosition.length; index++) {
        const previousSegment = largestSegmentsAtPosition[index - 1];
        const segment = largestSegmentsAtPosition[index];
        if (!previousSegment || !segment) {
            throw new Error("Invalid segments found while calculating spacing between largest segments.");
        }
        const { holeSize: previousHoleSize } = getSegmentSizeAndHoleSize(previousSegment);
        const { holeSize: currentHoleSize } = getSegmentSizeAndHoleSize(segment);
        const previousLargestHoleSize = Math.max(previousHoleSize.x, previousHoleSize.y);
        const currentLargestHoleSize = Math.max(currentHoleSize.x, currentHoleSize.y);
        const totalSpacing = keepout + previousLargestHoleSize / 2 + currentLargestHoleSize / 2;
        spacingBetweenLargestSegments.push(totalSpacing);
    }
    // Using a zig-zag pattern to arrange the children, so only concerned with the first two segments
    const spacing = spacingBetweenLargestSegments[0] ?? keepout;
    const position1 = new Vector2();
    const position2 = position1
        .clone()
        .add(new Vector2(spacing, 0).rotateAround(position1, smartViaBakedRules.orientation));
    return [position1, position2];
}
/**
 * Return trace layers for the children of a smart via node
 * Each segment is connected to the next by a copper trace, and the layer of their connection.
 */
export function getSmartViaTraceChildrenLayers(selectedViaConfigs, layers) {
    return selectedViaConfigs.reduce((traceLayers, viaConfig, viaConfigIndex) => {
        // Skip the first config since it doesn't need to join to any previous layer
        if (viaConfigIndex === 0)
            return traceLayers;
        // For the next configs, get the first connected layer.
        // We want a trace here!
        const firstSegment = viaConfig.segments.at(0);
        if (firstSegment) {
            const firstLayerIndex = firstSegment.startLayer - 1;
            const firstLayer = layers[firstLayerIndex];
            if (firstLayer) {
                traceLayers.push(firstLayer);
            }
        }
        return traceLayers;
    }, []);
}
function getStackupInfoFromLayoutNodeBakedRules(bakedRules) {
    const stackup = bakedRules?.stackup;
    const viaConfigsMap = bakedRules?.viaConfigs;
    const viaHoleConfigs = bakedRules?.viaHoleConfigs;
    if (!stackup || !viaConfigsMap || !viaHoleConfigs) {
        return {
            copperLayers: [],
            viaConfigs: [],
            viaConfigsMap: {},
            viaHoleConfigs: [],
        };
    }
    const copperLayers = getOrderedCopperLayers(stackup);
    const viaConfigs = Object.values(viaConfigsMap).sort((a, b) => a.order - b.order);
    // Normalize and cache the via hole configs so we don't recompute them every time
    const normalizedViaHoleConfigs = viaHoleConfigs.reduce((acc, holeConfig) => {
        acc.push({
            ...holeConfig,
            minSize: getNormalizedLengthValue(holeConfig.minSize),
            holeSize: getNormalizedLengthValue(holeConfig.holeSize),
        });
        return acc;
    }, []);
    return {
        copperLayers,
        viaConfigs,
        viaConfigsMap,
        viaHoleConfigs: normalizedViaHoleConfigs,
    };
}
/**
 * Returns the copper layers and via configurations for the top-level layout node.
 * Optionally, the results can be cached to avoid recomputation.
 */
export function getStackupLayersAndViaConfigs(bakedRules, pcbNodes, stackupInfoCache) {
    if ("stackup" in bakedRules) {
        return getStackupInfoFromLayoutNodeBakedRules(bakedRules);
    }
    const layoutNodeUid = bakedRules.topLevelLayoutUid;
    // Return cached result if available
    const cachedResult = layoutNodeUid && stackupInfoCache?.get(layoutNodeUid);
    if (cachedResult) {
        return cachedResult;
    }
    const layoutNode = layoutNodeUid ? pcbNodes[layoutNodeUid] : undefined;
    const stackupInfo = getStackupInfoFromLayoutNodeBakedRules(layoutNode?.bakedRules);
    // Cache the result
    if (layoutNodeUid) {
        stackupInfoCache?.set(layoutNodeUid, stackupInfo);
    }
    return stackupInfo;
}
export function getNormalizedLengthValue(value) {
    const parser = new RuleParser({ size: { key: "size", uid: "size", value } });
    const parsed = parser.parseRules(["size"]);
    return parsed.size.x;
}
export function getViaConfigConnectedLayers(viaConfig, copperLayers) {
    return copperLayers.filter((_copperLayer, index) => {
        const layerOrder = index + 1;
        return viaConfig.segments.some((segment) => isViaSegmentConnectingLayer(segment, layerOrder));
    });
}
//# sourceMappingURL=utils.js.map